/**
 * Orchestrator.
 *
 * - applies theme to root via CSS custom properties (once)
 * - mounts a single shared progress bar element + a single shared step element
 * - iterates config.steps sequentially: for each, dynamic-imports the
 *   matching step module (one chunk per type), mounts it, awaits onComplete
 * - between steps: calls handle.unmount() (timers/listeners) then
 *   container.replaceChildren() (DOM cleanup — orchestrator owns this)
 * - on final completion: delegates to options.onFinalDelivery
 *
 * Step modules are referenced only via dynamic `import()` so Vite splits
 * each into its own chunk. Never statically import them.
 *
 * 4.4 added MountQuizOptions:
 *   - signal:           AbortSignal for mid-mount cancellation. This is the
 *                       PREVIEW RE-RENDER PATH — when Studio sends a new
 *                       QUIZ_PREVIEW_CONFIG_UPDATED while the previous quiz
 *                       is still rendering, src/runtime/preview.ts aborts
 *                       the in-flight mount and starts a fresh one. runStep
 *                       below ensures the active step's handle.unmount()
 *                       fires regardless of why the abort happened.
 *   - emit:             override the analytics-stub emit (preview suppresses).
 *   - onFinalDelivery:  override the delivery redirect (preview replaces
 *                       with a "preview complete" inline message).
 * Production callers pass no options; defaults preserve 4.3 behavior.
 */

import { createAnalyticsEngine } from '@/analytics/engine';
import type { AnalyticsEmit, AnalyticsTransport } from '@/analytics/types';
import { isSafeUrl } from '@/lib/safe-url';
import { redirectToDelivery } from '@/runtime/delivery';
import type { AnyRuntimeStep, RuntimeQuizConfig } from '@/types/runtime';
import type { StepContext, StepEvent, StepHandle, StepResult } from '@/types/step-context';
import { applyTheme } from '@/ui/theme';
import { renderProgressBar } from '@/ui/progress-bar';

export interface MountQuizOptions {
  signal?: AbortSignal;
  /**
   * Analytics sink override. When set, every analytics event (step-scoped
   * AND the orchestrator's quiz_started/quiz_completed/cta_clicked) routes
   * here and no engine is created. Preview passes a no-op; tests pass a spy.
   * When omitted, the default engine sink is built from `config` + `transport`.
   */
  emit?: (event: AnalyticsEmit) => void;
  /**
   * Transport for the default engine sink. Ignored when `emit` is set.
   * Defaults to the Phase-1 StubTransport; Phase 2 passes the Beacon /
   * postMessage adapter here.
   */
  transport?: AnalyticsTransport;
  /**
   * Final-step delivery hook. `root` is passed so overrides can swap in
   * their own end-of-quiz UI (e.g. preview's "Restart quiz" surface).
   */
  onFinalDelivery?: (config: RuntimeQuizConfig, root: HTMLElement) => void;
}

// Delay between the last step completing and the actual navigation. No
// interstitial is rendered in production — the end user goes straight to
// the offer; the delay only exists so the analytics engine's
// quiz_completed flush lands before the page is torn down. The
// "Redirecting…" card is a PREVIEW-ONLY surface (see preview.ts), where
// it stands in for a navigation that never happens.
const PRODUCTION_REDIRECT_DELAY_MS = 1200;

const defaultFinalDelivery = (config: RuntimeQuizConfig, _root: HTMLElement): void => {
  if(config.pgTrigger.enabled) {
    let win = window.top ? window.top : window;
    win.postMessage({
        type: 'pgRewardClicked',
        url: config.delivery.url
      },
      '*'
    );
  } else {
    window.setTimeout(() => redirectToDelivery(config.delivery), PRODUCTION_REDIRECT_DELAY_MS);
  }
};

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

async function runStep(
  container: HTMLElement,
  step: AnyRuntimeStep,
  locale: string,
  emit: (event: StepEvent) => void,
  signal: AbortSignal | undefined,
): Promise<StepResult> {
  return new Promise<StepResult>((resolve, reject) => {
    let handle: StepHandle | null = null;
    let completed = false;

    // Single termination path. `cb` is the resolve or reject. `completed`
    // guards against late aborts after onComplete (and vice-versa) so
    // handle.unmount() runs exactly once.
    const finish = (cb: () => void): void => {
      if (completed) return;
      completed = true;
      handle?.unmount();
      cb();
    };

    // Abort handler — fires when the orchestrator's signal is aborted (the
    // PREVIEW RE-RENDER PATH). MUST call handle.unmount() so the active
    // step's timers/listeners are torn down regardless of how far through
    // its lifecycle it is. {once: true} drops the listener after firing;
    // the completed flag covers the abort-after-complete race.
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('aborted', 'AbortError'));
        return;
      }
      signal.addEventListener(
        'abort',
        () => finish(() => reject(new DOMException('aborted', 'AbortError'))),
        { once: true },
      );
    }

    const ctx: StepContext = {
      locale,
      emit,
      onComplete: (result) => {
        if (completed) {
          console.warn('[quiz-runtime] onComplete called twice, ignoring', result);
          return;
        }
        finish(() => resolve(result));
      },
    };
    // Chat normalizes to per-MESSAGE analytics steps — the chat renderer
    // emits step_viewed for each message with its own stepId/stepIndex. Skip
    // the generic step-level step_viewed for chat so it doesn't create a
    // spurious container row colliding with message index 0. Every other step
    // type maps 1:1 and gets its step_viewed here.
    if (step.type !== 'chat') {
      ctx.emit({
        type: 'step_viewed',
        stepId: step.id,
        stepIndex: step.index,
        stepType: step.type,
      });
    }

    // Per-branch dynamic import. `step` is narrowed inside each case to the
    // variant the matching module's mount expects. Vite splits each
    // import() target into its own chunk.
    //
    // The `completed` check inside each .then() handles the race where
    // abort fires before the dynamic import resolves — we must NOT mount
    // a step into a container that's already being torn down.
    switch (step.type) {
      case 'question':
        void import('@/steps/question').then((m) => {
          if (completed) return;
          handle = m.mount(container, step, ctx);
        });
        return;
      case 'image':
        void import('@/steps/image').then((m) => {
          if (completed) return;
          handle = m.mount(container, step, ctx);
        });
        return;
      case 'spin_wheel':
        void import('@/steps/spin-wheel').then((m) => {
          if (completed) return;
          handle = m.mount(container, step, ctx);
        });
        return;
      case 'scratch_card':
        void import('@/steps/scratch-card').then((m) => {
          if (completed) return;
          handle = m.mount(container, step, ctx);
        });
        return;
      case 'chat':
        void import('@/steps/chat').then((m) => {
          if (completed) return;
          handle = m.mount(container, step, ctx);
        });
        return;
      // DEFERRED-V21: case 'lead_capture': import('@/steps/lead-capture')...
    }
    // Exhaustiveness check — validateConfig rejects unknown types upstream,
    // but if a new variant lands without updating the switch this fires at
    // compile time.
    const _exhaustive: never = step;
    throw new Error(`unhandled step type: ${JSON.stringify(_exhaustive)}`);
  });
}

function sendQuizIdToPGScript(config: RuntimeQuizConfig,): void {
  let win = window.top ? window.top : window;
  const interval = setInterval(() => {
    win.postMessage({ type: 'quizPing' }, '*');
  }, 500);

  window.addEventListener('message', event => {
    if (event.data.type === 'pgScriptLoaded') {
      clearInterval(interval);
      win.postMessage(
        {
          type: 'pgQuizLoaded',
          quizId: config.quizId,
        },
        '*',
      );
    }
  });
}

export async function mountQuiz(
  root: HTMLElement,
  config: RuntimeQuizConfig,
  options: MountQuizOptions = {},
): Promise<void> {
  const signal = options.signal;
  const onFinalDelivery = options.onFinalDelivery ?? defaultFinalDelivery;

  sendQuizIdToPGScript(config);
  
  // Analytics sink. An explicit `emit` override (preview no-op / test spy)
  // wins; otherwise build the engine (inert in preview) and route through it.
  // `track` accepts the full AnalyticsEmit union — step modules receive the
  // narrower StepEvent view of the same function.
  let track: (event: AnalyticsEmit) => void;
  if (options.emit) {
    track = options.emit;
  } else {
    const engine = createAnalyticsEngine(config, options.transport);
    track = (event) => engine.enqueue(event);
  }

  applyTheme(root, config.settings);

  // Chat is a self-contained mode — it must be the only step type in the
  // quiz. When any step is chat the quiz is chat-only, and
  // chrome (title + progress bar) is suppressed and root padding is
  // dropped so the conversation surface goes edge-to-edge — matches a
  // native chat app on mobile.
  const isChatMode = config.steps.some((s) => s.type === 'chat');
  if (isChatMode) {
    root.style.padding = '0';
    root.style.gap = '0';
  }

  // Header logo. Publisher-provided URL → centered <img> above the title,
  // only when the URL is safe (http/https) and non-empty. Suppressed in
  // chat mode, which renders its own header.
  const logoUrl = (config.header.logoUrl ?? '').trim();
  const logoEl = document.createElement('img');
  logoEl.setAttribute('data-quiz-logo', '');
  logoEl.alt = '';
  logoEl.loading = 'lazy';
  logoEl.style.display = 'none';
  logoEl.style.height = '40px';
  logoEl.style.maxWidth = '60%';
  logoEl.style.objectFit = 'contain';
  logoEl.style.margin = '0.25rem auto 0';
  if (!isChatMode && isSafeUrl(logoUrl)) {
    logoEl.src = logoUrl;
    logoEl.style.display = 'block';
  }

  const titleEl = document.createElement('h1');
  titleEl.textContent = config.header.title;
  // Vertically center the title in the band between the iframe edge and
  // the progress bar. #quiz-root's padding-top is 1.25rem, so a 1.5rem
  // top margin lands the title at ~2.75rem from the frame top with a
  // matching 1.25rem gap to the progress bar — reads as centered rather
  // than top-hugging on a phone preview.
  titleEl.style.margin = '1.5rem auto 1.25rem';
  titleEl.style.width = '100%';
  titleEl.style.maxWidth = '32rem';
  titleEl.style.fontSize = 'clamp(1.05rem, 0.9rem + 1vw, 1.35rem)';
  titleEl.style.fontWeight = '600';
  titleEl.style.color = 'var(--quiz-primary)';
  titleEl.style.lineHeight = '1.25';
  titleEl.style.textAlign = 'center';
  titleEl.style.textWrap = 'balance';
  // Empty title (publisher left it blank) → don't reserve vertical space.
  const titleShown = !!config.header.title && !isChatMode;
  if (!titleShown) titleEl.style.display = 'none';

  // Header subtitle — muted line under the title. Suppressed in chat mode
  // (chat owns its own chrome). Publisher-authored string → textContent.
  const subtitleText = (config.header.subtitle ?? '').trim();
  const subtitleEl = document.createElement('p');
  subtitleEl.setAttribute('data-quiz-subtitle', '');
  subtitleEl.textContent = subtitleText;
  subtitleEl.style.width = '100%';
  subtitleEl.style.maxWidth = '32rem';
  subtitleEl.style.fontSize = '0.85rem';
  subtitleEl.style.lineHeight = '1.35';
  subtitleEl.style.color = 'var(--quiz-text, #1a1a1a)';
  subtitleEl.style.opacity = '0.7';
  subtitleEl.style.textAlign = 'center';
  subtitleEl.style.textWrap = 'balance';
  const showSubtitle = !!subtitleText && !isChatMode;
  if (!showSubtitle) {
    subtitleEl.style.display = 'none';
  } else if (titleShown) {
    // Tighten the title→subtitle gap so they read as one header block,
    // and let the subtitle carry the gap down to the progress bar.
    titleEl.style.margin = '1.5rem auto 0.35rem';
    subtitleEl.style.margin = '0 auto 1.25rem';
  } else {
    // Subtitle without a visible title → it takes the title's spacing.
    subtitleEl.style.margin = '1.5rem auto 1.25rem';
  }

  const progressEl = document.createElement('div');
  progressEl.style.width = '100%';
  progressEl.style.flexShrink = '0';
  if (isChatMode) progressEl.style.display = 'none';

  // PG-trigger legal disclaimer. Mounted once as a sibling below the step
  // container and revealed only while the LAST step is on screen (the loop
  // below flips display). Both production (main.ts) and preview (preview.ts)
  // run this orchestrator, so behavior is identical in both. Publisher-
  // authored string → textContent, never innerHTML.
  const disclaimerText = config.pgTrigger.enabled ? config.pgTrigger.disclaimerText.trim() : '';
  const disclaimerEl = document.createElement('p');
  disclaimerEl.setAttribute('data-pg-disclaimer', '');
  disclaimerEl.textContent = disclaimerText;
  disclaimerEl.style.display = 'none';
  disclaimerEl.style.flexShrink = '0';
  disclaimerEl.style.width = '100%';
  disclaimerEl.style.maxWidth = '32rem';
  disclaimerEl.style.boxSizing = 'border-box';
  disclaimerEl.style.margin = '0.75rem auto 0';
  // Own horizontal/bottom inset so chat mode (root padding 0) doesn't
  // leave the text flush against the iframe edge.
  disclaimerEl.style.padding = '0 1rem 0.75rem';
  disclaimerEl.style.fontSize = '0.75rem';
  disclaimerEl.style.lineHeight = '1.4';
  disclaimerEl.style.textAlign = 'center';
  disclaimerEl.style.color = 'var(--quiz-text, #1a1a1a)';
  disclaimerEl.style.opacity = '0.65';

  const stepEl = document.createElement('div');
  stepEl.style.width = '100%';
  stepEl.style.flex = '1 1 auto';
  stepEl.style.display = 'flex';
  stepEl.style.flexDirection = 'column';
  // Vertical centering of step content inside the remaining viewport
  // space (after the progress bar). justify-content: center centers
  // the main axis (vertical here, because flex-direction: column).
  // align-items: center handles horizontal. When a step's section is
  // taller than the available area, flex centering still works but
  // the section overflows symmetrically; #quiz-root's min-height keeps
  // the document scrollable so nothing gets cropped.
  // Chat mode stretches instead of centering so the chat surface fills
  // the full viewport width.
  stepEl.style.justifyContent = isChatMode ? 'stretch' : 'center';
  stepEl.style.alignItems = isChatMode ? 'stretch' : 'center';

  // Footer links — persistent legal/nav links pinned to the bottom (stepEl's
  // flex-grow pushes them down). Real anchors opening in a new tab when the
  // URL is safe, label-only <span> otherwise. Publisher strings via
  // textContent. Suppressed in chat mode (edge-to-edge, its own chrome).
  const footerEl = document.createElement('nav');
  footerEl.setAttribute('data-quiz-footer', '');
  footerEl.style.flexShrink = '0';
  footerEl.style.width = '100%';
  footerEl.style.maxWidth = '32rem';
  footerEl.style.boxSizing = 'border-box';
  footerEl.style.margin = '0 auto';
  footerEl.style.display = 'flex';
  footerEl.style.flexWrap = 'wrap';
  footerEl.style.justifyContent = 'center';
  footerEl.style.gap = '0.75rem';
  footerEl.style.padding = '0.75rem 1rem';
  for (const link of isChatMode ? [] : config.footerLinks) {
    const label = (link.label ?? '').trim();
    if (!label) continue;
    const url = (link.url ?? '').trim();
    const linkEl: HTMLElement = isSafeUrl(url)
      ? Object.assign(document.createElement('a'), {
          href: url,
          target: '_blank',
          rel: 'noopener noreferrer',
        })
      : document.createElement('span');
    linkEl.textContent = label;
    linkEl.style.fontSize = '0.7rem';
    linkEl.style.color = 'var(--quiz-text, #1a1a1a)';
    linkEl.style.opacity = '0.6';
    linkEl.style.textDecoration = 'underline';
    footerEl.appendChild(linkEl);
  }
  if (footerEl.childElementCount === 0) footerEl.style.display = 'none';

  root.replaceChildren(logoEl, titleEl, subtitleEl, progressEl, stepEl, disclaimerEl, footerEl);

  // First user-visible step is about to render → quiz_started precedes its
  // step_viewed. (quiz_loaded is intentionally not emitted.)
  if (config.steps.length > 0) track({ type: 'quiz_started' });

  try {
    for (let i = 0; i < config.steps.length; i++) {
      if (signal?.aborted) return;
      const step = config.steps[i];
      if (!step) continue;

      if (config.settings.progressBar) {
        renderProgressBar(progressEl, i, config.steps.length);
      }

      if (disclaimerText && i === config.steps.length - 1) {
        disclaimerEl.style.display = '';
      }

      const result = await runStep(stepEl, step, config.locale, track, signal);
      console.info(`[quiz-runtime] step ${i} (${step.type}) completed`, result);
      stepEl.replaceChildren();
    }
  } catch (e) {
    // Swallow ONLY AbortError — that's the documented preview re-render
    // path. Any other failure (step renderer bug, exhaustiveness violation,
    // etc.) must surface so we don't silently mask real problems.
    if (isAbortError(e)) return;
    throw e;
  }

  if (signal?.aborted) return;
  if (config.settings.progressBar) {
    renderProgressBar(progressEl, config.steps.length - 1, config.steps.length);
  }

  // Completion analytics, attributed to the last step so it feeds the
  // funnel's final-step drop-off and the completion-rate numerator
  // (final_cta_clicks / step1_users). cta_clicked then quiz_completed; the
  // engine flushes immediately on quiz_completed before the redirect.
  const lastStep = config.steps[config.steps.length - 1];
  if (lastStep) {
    track({
      type: 'cta_clicked',
      stepId: lastStep.id,
      stepIndex: lastStep.index,
      stepType: lastStep.type,
      optionId: 'final_cta',
      optionKind: 'final_cta',
    });
    track({ type: 'quiz_completed' });
  }

  onFinalDelivery(config, root);
}
